import React, { useCallback, useMemo } from 'react'
import { useStyles } from './HelpContent.styles'
import { Link, Typography } from '@mui/material'
import { clsx } from 'clsx'
import DOMPurify from 'dompurify'
import { HelpContentData, HelpTextData } from '../HelpDrawer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLink, faUser } from '@fortawesome/pro-regular-svg-icons'
import {
  CollapsibleHelpContent,
  isCollapsiblePersonaBlock,
} from './CollapsibleHelpContent'

// DOMPurify hook types differ between dompurify versions in strict mode.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(DOMPurify as any).addHook(
  'afterSanitizeAttributes',
  function (node: HTMLElement) {
    // set all elements owning target to target=_blank
    if ('target' in node) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener')
    }
  }
)
export type HelpContentProps = {
  helpContent?: HelpContentData[]
}
export const HelpContent = React.memo(
  ({ helpContent = [] }: HelpContentProps) => {
    const { classes } = useStyles()
    return (
      <div className={classes.container} data-testid="content">
        {helpContent.map(help => (
          <HelpData helpData={help} />
        ))}
      </div>
    )
  }
)

export type HelpDataProps = {
  helpData: HelpContentData
}
export const HelpData = React.memo(({ helpData }: HelpDataProps) => {
  const { classes } = useStyles()
  const hasQuestion = !!helpData.question
  const answersList = helpData.helpAnswer
  const collapsible =
    answersList != null && isCollapsiblePersonaBlock(helpData, answersList)
  const persona = collapsible ? answersList[0] : undefined

  const collapsibleHeader = useMemo(
    () => (persona ? <Answers answers={persona} /> : null),
    [persona]
  )

  const faqSectionsStable = useMemo(() => {
    if (!collapsible || !answersList) return []
    return answersList.slice(1)
  }, [collapsible, answersList])

  const renderCollapsibleFaqAnswer = useCallback(
    (data: HelpTextData, j: number) => (
      <Answers key={j} answers={data} hasNoQuestion={false} />
    ),
    []
  )

  if (collapsible) {
    return (
      <CollapsibleHelpContent
        header={collapsibleHeader}
        faqSections={faqSectionsStable}
        renderFaqAnswer={renderCollapsibleFaqAnswer}
      />
    )
  }

  return (
    <>
      {hasQuestion ? (
        <div className={classes.qnaContainer}>
          <div data-testid="question">
            <Typography className={classes.questionText}>
              {helpData.question}
            </Typography>
          </div>
          {(helpData.helpAnswer ?? []).map(data => (
            <Answers answers={data} />
          ))}
        </div>
      ) : (
        (helpData.helpAnswer ?? []).map(data => (
          <Answers answers={data} hasNoQuestion={!hasQuestion} />
        ))
      )}
    </>
  )
})

export type AnswersProps = {
  answers: HelpTextData
  hasNoQuestion?: boolean
}
export const Answers = React.memo(
  ({ answers, hasNoQuestion }: AnswersProps) => {
    const { classes } = useStyles()
    const sanitizer = DOMPurify.sanitize
    switch (answers.type) {
      case 'faq':
        return <></>
      case 'persona': {
        const variant = answers.badgeVariant
        const label = answers.label?.trim()
        if (!label || (variant !== 'lightBlue' && variant !== 'purple')) {
          return <></>
        }
        const badgeClass =
          variant === 'lightBlue'
            ? classes.roleBadgeLightBlue
            : classes.roleBadgePurple
        return (
          <div
            className={clsx(
              badgeClass,
              hasNoQuestion && classes.personaWithNoQuestion
            )}
            data-testid="help-persona-pill"
          >
            <FontAwesomeIcon
              icon={faUser}
              className={classes.personaBadgeIcon}
            />
            <Typography className={classes.personaBadgeLabel}>
              {label}
            </Typography>
          </div>
        )
      }
      case 'text': {
        return (
          <div>
            <div
              data-testid="answer-text"
              className={clsx(classes.questionText, classes.answerText)}
              dangerouslySetInnerHTML={{
                __html: sanitizer(answers?.text || ''),
              }}
            />
          </div>
        )
      }
      case 'link': {
        return (
          <div>
            <Link
              target="_blank"
              className={classes.link}
              href={answers?.link || ''}
              data-testid="link"
            >
              <Typography
                className={clsx(
                  classes.questionText,
                  classes.answerText,
                  classes.linkText
                )}
              >
                {answers?.linkText || ''}
              </Typography>
            </Link>
          </div>
        )
      }
      case 'textWithLink': {
        return (
          <div className={hasNoQuestion ? classes.withoutQuestion : ''}>
            <Typography
              className={clsx(classes.questionText, classes.answerText)}
            >
              {answers.text}
              <Link
                target="_blank"
                className={classes.link}
                href={answers?.link || ''}
                data-testid="link"
              >
                <span
                  className={clsx(
                    classes.questionText,
                    classes.answerText,
                    classes.linkText
                  )}
                >
                  {answers.linkText}
                  <span className={classes.icon}>
                    <FontAwesomeIcon
                      icon={faExternalLink}
                      style={{ width: '12px' }}
                    />
                  </span>
                </span>
              </Link>
            </Typography>
          </div>
        )
      }
      default:
        return <></>
    }
  }
)
